import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Package,
  Upload,
  Download,
  RefreshCcw,
  Edit2,
  Check,
  X,
  MoreHorizontal,
  Link2,
} from "lucide-react";

import { API_BASE_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";
import { apiFetch, downloadFile } from "../../utils/api";
import { fmtCurrency } from "../../utils/format";
import { Alert } from "../../components/Alert";
import { LinkOffersModal } from "./LinkOffersModal";

const normalizeEntry = (entry) => {
  const asNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };
  const normalizedOffers = Array.isArray(entry.linked_offers)
    ? entry.linked_offers.map((offer) => ({
        ...offer,
        current_price: asNumber(offer.current_price),
        available: Number.isFinite(Number(offer.available)) ? Number(offer.available) : null,
      }))
    : [];
  return {
    ...entry,
    symbol: entry.symbol ?? "",
    ean: entry.ean ?? "",
    net_price: asNumber(entry.net_price),
    vat_value: asNumber(entry.vat_value),
    gross_price: asNumber(entry.gross_price),
    vat_rate: asNumber(entry.vat_rate),
    linked_offers: normalizedOffers,
  };
};

export const ProductCollection = () => {
  const { authToken, authFetch } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const fileInputRef = useRef(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuButtonRef = useRef(null);
  const menuPanelRef = useRef(null);
  const [linkModalProduct, setLinkModalProduct] = useState(null);

  const loadEntries = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`${API_BASE_URL}/api/products/`, { token: authToken });
      const list = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      setEntries(list.map(normalizeEntry));
    } catch (e) {
      setError(e.message || "Failed to fetch products.");
      setMessage(null);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (authToken) {
      loadEntries();
    }
  }, [authToken, loadEntries]);

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    if (!editingId) return;
    const exists = entries.some((entry) => entry.id === editingId);
    if (!exists) {
      setEditingId(null);
      setEditDraft(null);
      setSavingEdit(false);
    }
  }, [editingId, entries]);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const handleClick = (event) => {
      if (
        menuButtonRef.current?.contains(event.target) ||
        menuPanelRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openMenuId]);

  const startEditing = useCallback((entry) => {
    setEditingId(entry.id);
    setEditDraft({
      symbol: entry.symbol ?? "",
      ean: entry.ean ?? "",
      net_price: entry.net_price != null ? entry.net_price.toString() : "",
      vat_value: entry.vat_value != null ? entry.vat_value.toString() : "",
      gross_price: entry.gross_price != null ? entry.gross_price.toString() : "",
      vat_rate: entry.vat_rate != null ? entry.vat_rate.toString() : "",
    });
    setMessage(null);
  }, []);

  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditDraft(null);
    setSavingEdit(false);
  }, []);

  const toggleMenu = useCallback((productId) => {
    setOpenMenuId((current) => (current === productId ? null : productId));
  }, []);

  const closeMenu = useCallback(() => setOpenMenuId(null), []);

  const openLinkModalForProduct = useCallback(
    (entry) => {
      setLinkModalProduct(entry);
      closeMenu();
    },
    [closeMenu],
  );

  const closeLinkModal = useCallback(() => setLinkModalProduct(null), []);

  const handleDraftChange = useCallback((field, value) => {
    setEditDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId || !editDraft || !authToken) return;

    const trimmedSymbol = editDraft.symbol.trim();
    const trimmedEan = editDraft.ean.trim();
    const toNumber = (value) => {
      if (value === "" || value == null) return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : NaN;
    };

    const netPrice = toNumber(editDraft.net_price);
    const vatValue = toNumber(editDraft.vat_value);
    const grossPrice = toNumber(editDraft.gross_price);
    const vatRate = toNumber(editDraft.vat_rate);

    if ([netPrice, vatValue, grossPrice, vatRate].some((value) => Number.isNaN(value))) {
      setMessage({ kind: "error", text: "Prices and VAT rate must be valid numbers." });
      return;
    }

    if ([netPrice, vatValue, grossPrice, vatRate].some((value) => value == null)) {
      setMessage({ kind: "error", text: "All price fields are required." });
      return;
    }

    setSavingEdit(true);
    setMessage(null);
    try {
      const payload = {
        symbol: trimmedSymbol || null,
        ean: trimmedEan || null,
        net_price: netPrice,
        vat_value: vatValue,
        gross_price: grossPrice,
        vat_rate: vatRate,
      };
      const updated = await apiFetch(`${API_BASE_URL}/api/products/${editingId}/`, {
        token: authToken,
        method: "PATCH",
        body: payload,
      });
      setEntries((prev) =>
        prev.map((entry) => (entry.id === updated.id ? normalizeEntry(updated) : entry))
      );
      setMessage({ kind: "success", text: "Product updated." });
      cancelEditing();
    } catch (e) {
      setMessage({ kind: "error", text: e.message || "Failed to update product." });
    } finally {
      setSavingEdit(false);
    }
  }, [editingId, editDraft, authToken, cancelEditing]);

  const handleLinksSaved = useCallback(async () => {
    await loadEntries();
    setMessage({ kind: "success", text: "Offer links updated." });
  }, [loadEntries]);

  const handleUpload = useCallback(
    async (file) => {
      if (!file || !authToken) return;
      setUploading(true);
      setMessage(null);
      setUploadSummary(null);
      try {
        const form = new FormData();
        form.append("file", file);
        const resp = await authFetch(`${API_BASE_URL}/api/products/upload-csv/`, {
          method: "POST",
          body: form,
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const detail = data?.detail || data?.error || data?.message || `HTTP ${resp.status}`;
          throw new Error(detail);
        }
        const summary = {
          created: data?.created ?? 0,
          updated: data?.updated ?? 0,
          errors: Array.isArray(data?.errors) ? data.errors : [],
        };
        setUploadSummary(summary);
        const base = `Created ${summary.created}, updated ${summary.updated}.`;
        setMessage({
          kind: summary.errors.length > 0 ? "info" : "success",
          text: summary.errors.length > 0
            ? `${base} Some rows contained errors.`
            : `Import completed successfully. ${base}`,
        });
        await loadEntries();
      } catch (e) {
        setMessage({ kind: "error", text: e.message || "Import failed." });
        setUploadSummary(null);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [authFetch, authToken, loadEntries]
  );

  const handleDownload = useCallback(
    async (variant) => {
      if (!authToken) return;
      const isTemplate = variant === "template";
      const setLoading = isTemplate ? setDownloadingTemplate : setDownloadingAll;
      setLoading(true);
      setMessage(null);
      try {
        const query = isTemplate ? "?template=1" : "";
        const filename = isTemplate ? "products_template.xlsx" : "products.csv";
        await downloadFile(
          `${API_BASE_URL}/api/products/download-csv/${query}`,
          authToken,
          filename
        );
        const successText = isTemplate ? "Template XLSX downloaded." : "CSV file downloaded.";
        setMessage({ kind: "success", text: successText });
      } catch (e) {
        const fallbackMessage = isTemplate
          ? "Failed to download the XLSX template."
          : "Failed to download the CSV file.";
        setMessage({ kind: "error", text: e.message || fallbackMessage });
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  const handleRefresh = useCallback(() => {
    if (!authToken) return;
    loadEntries();
  }, [authToken, loadEntries]);

  const hasEntries = entries.length > 0;

  return (
    <div className="flex h-[calc(100vh-6.1rem)] flex-col">
      <div className="flex-1 overflow-hidden rounded-2xl bg-white shadow">
        <div className="flex h-full flex-col gap-4 p-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center text-2xl font-bold text-gray-800">
              <Package className="mr-2 text-slate-500" size={26} /> Products
            </h2>

            <div className="flex items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-200">
                <Upload size={16} />
                {uploading ? "Importing..." : "Import CSV"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                  disabled={uploading}
                />
              </label>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-60"
              >
                <RefreshCcw size={16} />
                {loading ? "Refreshing..." : "Refresh"}
              </button>
              <button
                onClick={() => handleDownload("all")}
                disabled={downloadingAll}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                <Download size={16} />
                {downloadingAll ? "Downloading..." : "Download CSV"}
              </button>
              <button
                onClick={() => handleDownload("template")}
                disabled={downloadingTemplate}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-100 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                <Download size={16} />
                {downloadingTemplate ? "Downloading..." : "Download template"}
              </button>
            </div>
          </div>

          {message && <Alert kind={message.kind}>{message.text}</Alert>}
          {error && <Alert kind="error">{error}</Alert>}

          {uploadSummary?.errors?.length > 0 && (
            <div>
              <Alert kind="info">
                <div className="mb-2 font-semibold">Import errors (showing up to 10):</div>
                <ul className="list-disc space-y-1 pl-5">
                  {uploadSummary.errors.slice(0, 10).map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
                {uploadSummary.errors.length > 10 && (
                  <div className="mt-2 text-xs text-gray-600">
                    ...and {uploadSummary.errors.length - 10} more.
                  </div>
                )}
              </Alert>
            </div>
          )}

          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-600">
                Loading collection...
              </div>
            ) : !hasEntries ? (
              <div className="flex h-full flex-col items-center justify-center px-4 text-center text-sm text-gray-600">
                No products saved yet. Download the XLSX template above and import the completed data.
              </div>
            ) : (
              <div className="h-full overflow-auto">
                <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-700">
                <th className="px-4 py-3 font-semibold border-b">EAN</th>
                <th className="px-4 py-3 font-semibold border-b">Symbol</th>
                <th className="px-4 py-3 font-semibold border-b">Net purchase price</th>
                <th className="px-4 py-3 font-semibold border-b">VAT value</th>
                <th className="px-4 py-3 font-semibold border-b">Gross purchase price</th>
                <th className="px-4 py-3 font-semibold border-b">VAT rate</th>
                <th className="px-4 py-3 font-semibold border-b">Linked offers</th>
                <th className="px-4 py-3 font-semibold border-b text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.map((entry) => {
                const isEditing = editingId === entry.id;
                const draft = isEditing ? editDraft || {} : null;
                return (
                  <tr key={entry.id} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3 text-gray-800">
                      {isEditing ? (
                        <input
                          type="text"
                          value={draft?.ean ?? ""}
                          onChange={(e) => handleDraftChange("ean", e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="EAN"
                        />
                      ) : (
                        entry.ean || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800">
                      {isEditing ? (
                        <input
                          type="text"
                          value={draft?.symbol ?? ""}
                          onChange={(e) => handleDraftChange("symbol", e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="Symbol"
                        />
                      ) : (
                        entry.symbol || "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={draft?.net_price ?? ""}
                          onChange={(e) => handleDraftChange("net_price", e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="Net price"
                        />
                      ) : entry.net_price != null ? (
                        fmtCurrency(entry.net_price)
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={draft?.vat_value ?? ""}
                          onChange={(e) => handleDraftChange("vat_value", e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="VAT value"
                        />
                      ) : entry.vat_value != null ? (
                        fmtCurrency(entry.vat_value)
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={draft?.gross_price ?? ""}
                          onChange={(e) => handleDraftChange("gross_price", e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="Gross price"
                        />
                      ) : entry.gross_price != null ? (
                        fmtCurrency(entry.gross_price)
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={draft?.vat_rate ?? ""}
                          onChange={(e) => handleDraftChange("vat_rate", e.target.value)}
                          className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="VAT rate %"
                        />
                      ) : entry.vat_rate != null ? (
                        `${entry.vat_rate.toFixed(2)}%`
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800">
                      {entry.linked_offers.length > 0 ? (
                        <div className="space-y-2">
                          {entry.linked_offers.slice(0, 3).map((offer) => (
                            <div
                              key={offer.id}
                              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 shadow-sm"
                            >
                              <div className="font-semibold text-slate-900">{offer.name || "—"}</div>
                              <div className="flex flex-wrap gap-x-2 text-[0.7rem] text-slate-500">
                                <span>ID: {offer.offer_id}</span>
                                {offer.sku ? <span>SKU: {offer.sku}</span> : null}
                              </div>
                              <div className="text-[0.7rem] text-slate-500">
                                Price:{" "}
                                {offer.current_price != null ? fmtCurrency(offer.current_price) : "—"}
                              </div>
                            </div>
                          ))}
                          {entry.linked_offers.length > 3 && (
                            <div className="text-xs text-slate-500">
                              +{entry.linked_offers.length - 3} more offer
                              {entry.linked_offers.length - 3 === 1 ? "" : "s"}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-500">No offers linked</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-800">
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            disabled={savingEdit}
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                          >
                            <Check size={14} />
                            {savingEdit ? "Saving..." : "Save"}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditing}
                            disabled={savingEdit}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                          >
                            <X size={14} />
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="relative flex justify-center">
                          <button
                            type="button"
                            ref={openMenuId === entry.id ? menuButtonRef : undefined}
                            onClick={() => toggleMenu(entry.id)}
                            className="inline-flex items-center rounded-full border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                            aria-haspopup="menu"
                            aria-expanded={openMenuId === entry.id}
                          >
                            <MoreHorizontal size={18} />
                          </button>
                          {openMenuId === entry.id && (
                            <div
                              ref={menuPanelRef}
                              className="absolute right-0 top-10 z-20 w-44 rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg"
                              role="menu"
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  startEditing(entry);
                                  closeMenu();
                                }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 hover:bg-slate-50"
                              >
                                <Edit2 size={14} />
                                Edit product
                              </button>
                              <button
                                type="button"
                                onClick={() => openLinkModalForProduct(entry)}
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 hover:bg-slate-50"
                              >
                                <Link2 size={14} />
                                Link offers
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {linkModalProduct && (
        <LinkOffersModal
          product={linkModalProduct}
          authToken={authToken}
          authFetch={authFetch}
          onClose={closeLinkModal}
          onComplete={handleLinksSaved}
        />
      )}
    </div>
  );
};

export default ProductCollection;
